import React, { memo } from 'react'

import ViewPreviousHistoryModal from './ViewPreviousHistoryModal'
import useTranslations from '../../../hooks/useTranslations'
import ANTDButton from '../../../shared/antd/ANTDButton'
import ANTDColumn from '../../../shared/antd/ANTDColumn'
import { ANTDDateRange } from '../../../shared/antd/ANTDDatePicker'
import ANTDRow from '../../../shared/antd/ANTDRow'
import ANTDSelect from '../../../shared/antd/ANTDSelect'
import ANTDTable, {
  ANTDTableCell,
  ANTDTableRow,
} from '../../../shared/antd/ANTDTable'
import Label from '../../../shared/Label'
import { numberFormatting } from '../../../utils/customFunctions'
import { formatDate, SAVE_FORMAT } from '../../../utils/dayjs'
import { length } from '../../../utils/javascript'
import salesReport from '../container/salesReport.container'

const SalesReport = ({
  activeTab,
  viewPreviousHistory,
  setViewPreviousHistory,
}) => {
  const {
    dateRangeProps,
    processDate,
    salesReportList,
    selectDealerProps,
    selectLocationProps,
    selectChannelProps,
    fiscalYearSelector,
    receiptVehicleDate,
    handleTableChange,
    onProcessDateChange,
    onReceiptVehicleDateChange,
    onGenerateReport,
  } = salesReport({ activeTab })
  const { t } = useTranslations()
  const summaryOptions = [{ label: 'rpt_Sum', value: 'sum' }]
  const tableData = length(salesReportList?.list)
    ? salesReportList?.list?.map(val => ({
        ...val,
        key: val?.evNumber,
      }))
    : []

  const summaryDropdown = (
    <ANTDSelect
      style={{ width: 100 }}
      options={summaryOptions?.map(({ label, value }) => ({
        label: t(label),
        value,
      }))}
      defaultValue={summaryOptions[0].value}
      value={summaryOptions[0].value}
    />
  )

  const columns = [
    { title: t('job_EVNo'), dataIndex: 'evNumber', key: 'evNo' },
    {
      title: t('job_DealerName'),
      dataIndex: 'dealer',
      key: 'dealer',
      render: rowData => {
        return rowData?.businessName
      },
    },
    { title: t('inv_Location'), dataIndex: 'locationName', key: 'location' },
    { title: t('user_Channel'), dataIndex: 'channelName', key: 'channel' },
    {
      title: t('job_ConsumerName'),
      dataIndex: 'consumer',
      key: 'consumer',
      render: rowData => {
        return rowData?.lastName
      },
    },
    {
      title: t('job_VehicleRegistrationNumber'),
      dataIndex: 'vehicleRegistrationNumber',
      key: 'regNo',
    },
    { title: t('job_ModelName'), dataIndex: 'modelName', key: 'model' },
    {
      title: t('job_FuelType'),
      dataIndex: 'fuelTypeKey',
      key: 'fuel',
      render: rowData => t(rowData),
    },
    {
      title: t('job_BuyingPrice'),
      dataIndex: 'buyingPrice',
      key: 'buyingPrice',
      render: rowData => (
        <p className="nowrap">
          {rowData ? `₹ ${numberFormatting(rowData)}` : '-'}
        </p>
      ),
    },
    {
      title: t('job_BaseBuyingPrice'),
      dataIndex: 'baseBuyingPrice',
      key: 'baseBuyingPrice',
      render: rowData => (
        <p className="nowrap">
          {rowData ? `₹ ${numberFormatting(rowData)}` : '-'}
        </p>
      ),
    },
    {
      title: t('job_Commission'),
      dataIndex: 'commission',
      key: 'commission',
      width: 220,
      render: rowData => (
        <p className="nowrap">
          {rowData ? `₹ ${numberFormatting(rowData)}` : '-'}
        </p>
      ),
    },
    {
      title: t('job_AdditionalIncentive'),
      dataIndex: 'additionalIncentive',
      key: 'additionalIncentive',
      render: rowData => (
        <p className="nowrap">
          {rowData ? `₹ ${numberFormatting(rowData)}` : '-'}
        </p>
      ),
    },
    {
      title: t('job_Freight'),
      dataIndex: 'freight',
      key: 'freight',
      render: rowData => (
        <p className="nowrap">
          {rowData ? `₹ ${numberFormatting(rowData)}` : '-'}
        </p>
      ),
    },
    {
      title: t('dvz_Total'),
      dataIndex: 'total',
      key: 'total',
      render: rowData => (
        <p className="nowrap">
          {rowData ? `₹ ${numberFormatting(rowData)}` : '-'}
        </p>
      ),
    },
  ]

  return (
    <div>
      <div className="d-flex justify-content-end">
        <div>
          <div>
            <Label className="w-100" text={t('job_DealerName')} />
            <ANTDSelect className="w-100 mt-5" {...selectDealerProps} />
            <ANTDRow gutter={10}>
              <ANTDColumn md={12} xs={24}>
                <Label text={t('inv_Location')} />
                <ANTDSelect className="w-100 mt-5" {...selectLocationProps} />
              </ANTDColumn>
              <ANTDColumn md={12} xs={24}>
                <Label text={t('user_Channel')} />
                <ANTDSelect className="w-100 mt-5" {...selectChannelProps} />
              </ANTDColumn>
            </ANTDRow>
          </div>

          <ANTDRow gutter={10} className="align-end">
            <ANTDColumn md={12} xs={24}>
              <Label text={t('txt_FiscalYear')} />
              <ANTDSelect {...fiscalYearSelector} className="w-100 mt-5" />
            </ANTDColumn>
            <ANTDColumn md={12} xs={24}>
              <Label text={t('job_DateOfReceiptOfVehicle')} />
              <ANTDDateRange
                className="w-100 mt-5"
                allowClear={false}
                format={dateRangeProps.format}
                defaultValue={dateRangeProps.value}
                onChange={onReceiptVehicleDateChange}
                value={[
                  formatDate(receiptVehicleDate?.from, SAVE_FORMAT),
                  formatDate(receiptVehicleDate?.to, SAVE_FORMAT),
                ]}
              />
            </ANTDColumn>
          </ANTDRow>

          <div className="mt-10">
            <Label text={t('job_ProcessDate')} />
            <ANTDDateRange
              className="mt-5"
              {...dateRangeProps}
              allowClear
              onChange={onProcessDateChange}
              value={[
                formatDate(processDate?.from, SAVE_FORMAT),
                formatDate(processDate?.to, SAVE_FORMAT),
              ]}
            />
            <ANTDButton
              type="primary"
              className="ml-10"
              onClick={onGenerateReport}
            >
              {t('btn_GenerateSalesReport')}
            </ANTDButton>
            <ANTDButton
              type="primary"
              className="ml-10"
              onClick={() => setViewPreviousHistory({ open: true })}
            >
              {t('btn_ViewPreviousHistory')}
            </ANTDButton>
          </div>
        </div>
      </div>

      <div className="mt-15">
        <ANTDTable
          columns={columns}
          dataSource={tableData}
          scroll={{ x: 1500 }}
          pagination={{
            lastFetched: salesReportList?.pageNo,
            current: salesReportList?.pageNo,
            pageSize: 10,
            total: salesReportList?.lastPage * 10,
            responsive: true,
          }}
          onChange={handleTableChange}
          summary={() => (
            <ANTDTableRow>
              {columns.map(col => (
                <ANTDTableCell className="numeric-cell" key={col.key}>
                  {col.key === 'commission' ? (
                    <div className="d-flex align-center nowrap">
                      {summaryDropdown}
                      <span className="ml-10">
                        {numberFormatting(tableData?.[0]?.totalCommission) ||
                          '-'}
                      </span>
                    </div>
                  ) : (
                    '-'
                  )}
                </ANTDTableCell>
              ))}
            </ANTDTableRow>
          )}
        />
      </div>
      <ViewPreviousHistoryModal
        open={viewPreviousHistory.open}
        onClose={() => setViewPreviousHistory({ open: false })}
        payload={{
          sheetType: 'SALES_REPORT_SHEET',
        }}
      />
    </div>
  )
}

export default memo(SalesReport)
